import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  UserCheck,
  Search,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Building2,
  Briefcase,
  User,
  ShieldCheck,
  ChevronRight,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * ManagerApprovalSelector
 * Reusable component for Local Conveyance & Outstation Conveyance request forms.
 * Supports:
 *  - Selection from upward reporting hierarchy dropdown list
 *  - Employee ID Lookup with Enter key press
 *  - Full manager details preview card (Name, Code, Designation, Department, Division, Status)
 *  - Confirm/Select action & Reset/Change action
 *  - Complete server-side & client-side validation
 */
const ManagerApprovalSelector = ({
  selectedApproverId = '',
  onSelectApprover,
  eligibleManagers = [],
  targetEmployeeId = '',
  loadingManagers = false,
  docType = 'conveyance_request',
  label = 'Approval Manager *'
}) => {
  // Mode: 'dropdown' | 'lookup'
  const [mode, setMode] = useState('dropdown');

  // Lookup state
  const [inputCode, setInputCode] = useState('');
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [previewManager, setPreviewManager] = useState(null);

  // Confirmed Manager details object (for card view)
  const [confirmedManager, setConfirmedManager] = useState(null);

  // Whenever selectedApproverId changes or eligibleManagers load, resolve manager info
  useEffect(() => {
    if (!selectedApproverId) {
      setConfirmedManager(null);
      return;
    }

    // Try matching from eligibleManagers
    const matched = eligibleManagers.find(
      (m) => m.employee_id === selectedApproverId || m.user_id === selectedApproverId || m.employee_code === selectedApproverId
    );

    if (matched) {
      setConfirmedManager({
        employee_id: matched.employee_id,
        user_id: matched.user_id,
        employee_code: matched.employee_code || 'EMP',
        name: matched.name,
        designation: matched.designation || 'Manager',
        department: matched.department_name || matched.department || 'General',
        business_division: matched.business_division || 'Corporate',
        employment_status: 'Active',
        relationship_to_employee: matched.relationship_to_employee || 'Reporting Manager'
      });
    } else if (previewManager && (previewManager.employee_id === selectedApproverId || previewManager.user_id === selectedApproverId)) {
      setConfirmedManager(previewManager);
    }
  }, [selectedApproverId, eligibleManagers]);

  // Handle Employee ID lookup API call
  const handleLookupManager = async () => {
    const cleanCode = inputCode.trim();
    if (!cleanCode) {
      setLookupError('Please enter a valid Manager Employee ID.');
      toast.error('Please enter a valid Manager Employee ID.');
      return;
    }

    try {
      setLoadingLookup(true);
      setLookupError('');
      setPreviewManager(null);

      const url = `${API_URL}/approval-workflows/lookup-manager?manager_code=${encodeURIComponent(cleanCode)}${
        targetEmployeeId ? `&employee_id=${encodeURIComponent(targetEmployeeId)}` : ''
      }&doc_type=${encodeURIComponent(docType)}`;

      const { data } = await axios.get(url, { withCredentials: true });

      if (data.success && data.manager) {
        setPreviewManager(data.manager);
        setLookupError('');
        toast.success(`Manager Found: ${data.manager.name} (${data.manager.employee_code})`);
      } else {
        setLookupError('Manager not found. Please enter a valid Employee ID.');
        toast.error('Manager not found. Please enter a valid Employee ID.');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Manager not found. Please enter a valid Employee ID.';
      setLookupError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoadingLookup(false);
    }
  };

  // Handle KeyDown for Enter Key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLookupManager();
    }
  };

  // Confirm selected preview manager
  const handleConfirmManager = (mgrObj) => {
    setConfirmedManager(mgrObj);
    if (onSelectApprover) {
      onSelectApprover(mgrObj.employee_id, mgrObj);
    }
    toast.success(`Approving Manager set to ${mgrObj.name} (${mgrObj.employee_code})`);
  };

  // Reset / Change Manager
  const handleResetManager = () => {
    setConfirmedManager(null);
    setPreviewManager(null);
    setInputCode('');
    setLookupError('');
    if (onSelectApprover) {
      onSelectApprover('', null);
    }
  };

  return (
    <div className="p-4 bg-gradient-to-r from-blue-50/80 via-slate-50 to-indigo-50/70 border border-blue-200 rounded-2xl shadow-sm space-y-3 font-sans">
      {/* Label & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-blue-100 pb-2.5">
        <label className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-blue-600" />
          {label}
        </label>

        {/* Mode Switcher Tabs */}
        <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 shrink-0">
          <button
            type="button"
            onClick={() => { setMode('dropdown'); setLookupError(''); }}
            className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${
              mode === 'dropdown' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Hierarchy List
          </button>
          <button
            type="button"
            onClick={() => { setMode('lookup'); setLookupError(''); }}
            className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${
              mode === 'lookup' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Lookup Employee ID
          </button>
        </div>
      </div>

      {/* CONFIRMED MANAGER CARD DISPLAY */}
      {confirmedManager ? (
        <div className="bg-white rounded-xl p-3.5 border border-emerald-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-in fade-in duration-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-600 text-white font-black text-sm flex items-center justify-center shrink-0 shadow-sm">
              {confirmedManager.name ? confirmedManager.name.charAt(0).toUpperCase() : 'M'}
            </div>
            <div className="space-y-0.5 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-xs text-slate-900">{confirmedManager.name}</span>
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-mono font-bold text-[10px] rounded-md border border-emerald-200">
                  {confirmedManager.employee_code}
                </span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 font-bold text-[10px] rounded-md border border-blue-200">
                  {confirmedManager.relationship_to_employee || 'Approving Manager'}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2">
                <span>{confirmedManager.designation}</span>
                <span>•</span>
                <span>Dept: <strong>{confirmedManager.department}</strong></span>
                {confirmedManager.business_division && (
                  <>
                    <span>•</span>
                    <span>{confirmedManager.business_division}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
            <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 font-bold text-[11px] rounded-lg border border-emerald-200 inline-flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Selected
            </span>

            <button
              type="button"
              onClick={handleResetManager}
              className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] rounded-lg border border-slate-300 transition-colors flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3 text-slate-500" /> Change Manager
            </button>
          </div>
        </div>
      ) : (
        /* MODE A: DROPDOWN SELECTOR */
        mode === 'dropdown' ? (
          <div className="space-y-2">
            <div className="relative">
              <select
                value={selectedApproverId}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) {
                    onSelectApprover('', null);
                    return;
                  }
                  const matched = eligibleManagers.find((m) => m.employee_id === val || m.user_id === val);
                  if (matched) {
                    handleConfirmManager({
                      employee_id: matched.employee_id,
                      user_id: matched.user_id,
                      employee_code: matched.employee_code || 'EMP',
                      name: matched.name,
                      designation: matched.designation || 'Manager',
                      department: matched.department_name || matched.department || 'General',
                      relationship_to_employee: matched.relationship_to_employee || 'Reporting Manager'
                    });
                  } else {
                    onSelectApprover(val, null);
                  }
                }}
                className="w-full h-10 px-3 bg-white border border-slate-300 rounded-xl font-bold text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">-- Select Approving Manager from Hierarchy --</option>
                {eligibleManagers.map((m) => (
                  <option key={m.employee_id} value={m.employee_id}>
                    {m.name} [{m.employee_code || 'EMP'}] — {m.designation || 'Manager'} ({m.relationship_to_employee})
                  </option>
                ))}
              </select>
            </div>
            {loadingManagers && (
              <p className="text-[10px] text-blue-600 font-medium animate-pulse flex items-center gap-1">
                <RefreshCw className="w-3 h-3 animate-spin" /> Loading upward reporting hierarchy...
              </p>
            )}
            {!loadingManagers && eligibleManagers.length === 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 font-semibold flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <span>
                  No reporting manager is configured for this employee. Your request will be auto-routed to an administrator for approval.
                  You can also use the <strong>Lookup Employee ID</strong> tab above to manually search and select an approving manager.
                </span>
              </div>
            )}
          </div>
        ) : (
          /* MODE B: EMPLOYEE ID LOOKUP INPUT */
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input
                  type="text"
                  placeholder="Enter Manager Employee ID (e.g. EMP-1002) and press Enter..."
                  value={inputCode}
                  onChange={(e) => { setInputCode(e.target.value); setLookupError(''); }}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-mono font-bold text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 uppercase"
                />
              </div>

              <button
                type="button"
                onClick={handleLookupManager}
                disabled={loadingLookup || !inputCode.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 shrink-0"
              >
                {loadingLookup ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Lookup Manager
              </button>
            </div>

            {/* Inline Error Alert */}
            {lookupError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-semibold flex items-center gap-2 animate-in fade-in duration-150">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                <span>{lookupError}</span>
              </div>
            )}

            {/* Fetched Manager Preview Card */}
            {previewManager && (
              <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-md space-y-3 animate-in fade-in zoom-in duration-150">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Matching Manager Details Found</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    previewManager.employment_status === 'Active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {previewManager.employment_status || 'Active'}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-[10px] text-slate-400 block font-semibold">Manager Name</span>
                    <span className="font-extrabold text-slate-900">{previewManager.name}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-semibold">Employee ID / Code</span>
                    <span className="font-bold font-mono text-indigo-700">{previewManager.employee_code}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-semibold">Designation</span>
                    <span className="font-semibold text-slate-800">{previewManager.designation}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-semibold">Department</span>
                    <span className="font-semibold text-slate-800">{previewManager.department}</span>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-100 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewManager(null)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleConfirmManager(previewManager)}
                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl shadow flex items-center gap-1.5"
                  >
                    <CheckCircle className="w-3.5 h-3.5" /> Confirm &amp; Select Manager
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      )}

      <p className="text-[10px] text-slate-500">
        Requests will route directly to the selected manager's <strong>Approval Inbox</strong> for authorization.
      </p>
    </div>
  );
};

export default ManagerApprovalSelector;
